import { useState, useEffect } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Mail, Phone, Calendar, MessageSquare, Heart, Check, X, Clock } from "lucide-react";

const STATUS_COLORS = {
  pending: "bg-yellow-900/30 text-yellow-400 border-yellow-800/50",
  approved: "bg-green-900/30 text-green-400 border-green-800/50",
  declined: "bg-red-900/30 text-red-400 border-red-800/50",
  completed: "bg-blue-900/30 text-blue-400 border-blue-800/50",
};

export default function AdminMirrorLeads() {
  const [tab, setTab] = useState("contacts");
  const [contacts, setContacts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [scholarships, setScholarships] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [c, b, s] = await Promise.all([
        http.get("/mirror/admin/contacts"),
        http.get("/mirror/admin/bookings"),
        http.get("/mirror/admin/scholarships"),
      ]);
      setContacts(c.data || []);
      setBookings(b.data || []);
      setScholarships(s.data || []);
    } catch { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const updateScholarship = async (id, status) => {
    try {
      await http.patch(`/mirror/admin/scholarships/${id}`, { status });
      setScholarships(prev => prev.map(s => s.id === id ? { ...s, status } : s));
      toast.success(`Scholarship ${status}`);
    } catch { toast.error("Failed to update"); }
  };

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#C9A055]" /></div>;

  const tabs = [
    { key: "contacts", label: "Contact Leads", count: contacts.length },
    { key: "bookings", label: "Bookings", count: bookings.length },
    { key: "scholarships", label: "Scholarship Requests", count: scholarships.length },
  ];

  return (
    <div className="space-y-6" data-testid="admin-mirror-leads">
      <div>
        <h2 className="font-display text-3xl text-[#F4F1EA]">Leads & Bookings</h2>
        <p className="font-body text-[13px] text-[#8a857d] mt-1">All Mirror Conversations contact submissions, bookings, and scholarship requests.</p>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 flex-wrap">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`leads-tab-${t.key}`}
            className={`px-4 py-2 text-xs font-ui tracking-[0.15em] uppercase border transition-colors ${
              tab === t.key ? "bg-[#C9A055] text-black border-[#C9A055]" : "border-white/10 text-[#8a857d] hover:border-[#C9A055]/50"
            }`}>
            {t.label} <span className="ml-1 opacity-60">({t.count})</span>
          </button>
        ))}
      </div>

      {/* Content */}
      {tab === "contacts" && <ContactsList items={contacts} />}
      {tab === "bookings" && <BookingsList items={bookings} />}
      {tab === "scholarships" && <ScholarshipsList items={scholarships} onUpdate={updateScholarship} />}
    </div>
  );
}

function ContactsList({ items }) {
  if (!items.length) return <Empty text="No contact submissions yet." />;
  return (
    <div className="space-y-3">
      {items.map((c, i) => (
        <div key={c.id || i} className="bg-[#0a0a0a] border border-white/10 p-5 space-y-3" data-testid={`contact-row-${i}`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="font-ui text-sm text-[#F4F1EA] font-bold">{c.name}</h3>
              <div className="flex flex-wrap gap-4 mt-1 text-xs text-[#8a857d]">
                <span className="flex items-center gap-1"><Mail size={12} /> {c.email}</span>
                {c.phone && <span className="flex items-center gap-1"><Phone size={12} /> {c.phone}</span>}
              </div>
            </div>
            <div className="text-right shrink-0">
              <span className="font-ui text-[10px] tracking-[0.15em] uppercase text-[#C9A055]">{c.interested_in}</span>
              <div className="text-[10px] text-[#5a5650] mt-1">{fmtDate(c.created_at)}</div>
            </div>
          </div>
          <p className="text-sm text-[#d1cfc7] leading-relaxed whitespace-pre-line">{c.message}</p>
          {c.is_scholarship && (
            <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/20 p-3 rounded text-xs">
              <span className="text-[#D4AF37] font-bold uppercase tracking-wider">Scholarship Request</span>
              {c.scholarship_reason && <p className="text-[#d1cfc7] mt-1">{c.scholarship_reason}</p>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function BookingsList({ items }) {
  if (!items.length) return <Empty text="No bookings yet. When someone books via Stripe, they'll appear here." />;
  return (
    <div className="space-y-3">
      {items.map((b, i) => (
        <div key={b.id || i} className="bg-[#0a0a0a] border border-white/10 p-5" data-testid={`booking-row-${i}`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="font-ui text-sm text-[#F4F1EA] font-bold">{b.name}</h3>
              <span className="text-xs text-[#8a857d] flex items-center gap-1"><Mail size={12} /> {b.email}</span>
            </div>
            <div className="text-right">
              <span className="font-ui text-lg text-[#C9A055] font-bold">${b.amount}</span>
              <div className="text-[10px] text-[#5a5650]">{b.package_id || b.service_id}</div>
              <StatusBadge status={b.status} />
              <div className="text-[10px] text-[#5a5650] mt-1">{fmtDate(b.created_at)}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ScholarshipsList({ items, onUpdate }) {
  if (!items.length) return <Empty text="No scholarship requests yet." />;
  return (
    <div className="space-y-3">
      {items.map((s, i) => (
        <div key={s.id || i} className="bg-[#0a0a0a] border border-white/10 p-5 space-y-3" data-testid={`scholarship-row-${i}`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="font-ui text-sm text-[#F4F1EA] font-bold">{s.name}</h3>
              <div className="flex flex-wrap gap-4 mt-1 text-xs text-[#8a857d]">
                <span className="flex items-center gap-1"><Mail size={12} /> {s.email}</span>
                {s.phone && <span className="flex items-center gap-1"><Phone size={12} /> {s.phone}</span>}
              </div>
            </div>
            <div className="text-right shrink-0">
              <span className="font-ui text-[10px] tracking-[0.15em] uppercase text-[#C9A055]">{s.conversation_type}</span>
              <StatusBadge status={s.status} />
              <div className="text-[10px] text-[#5a5650] mt-1">{fmtDate(s.created_at)}</div>
            </div>
          </div>
          <div className="space-y-2 text-sm text-[#d1cfc7]">
            <div><span className="text-[#8a857d] text-xs uppercase tracking-wider">Why:</span> <span className="whitespace-pre-line">{s.reason}</span></div>
            <div><span className="text-[#8a857d] text-xs uppercase tracking-wider">Clarity goal:</span> <span className="whitespace-pre-line">{s.clarity_goal}</span></div>
            {s.preferred_times && <div><span className="text-[#8a857d] text-xs uppercase tracking-wider">Preferred times:</span> {s.preferred_times}</div>}
          </div>
          {s.status === "pending" && (
            <div className="flex gap-2 pt-2 border-t border-white/5">
              <button onClick={() => onUpdate(s.id, "approved")} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-green-800/50 text-green-400 hover:bg-green-900/30 transition-colors" data-testid={`approve-${i}`}>
                <Check size={12} /> Approve
              </button>
              <button onClick={() => onUpdate(s.id, "declined")} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-red-800/50 text-red-400 hover:bg-red-900/30 transition-colors" data-testid={`decline-${i}`}>
                <X size={12} /> Decline
              </button>
            </div>
          )}
          {s.status !== "pending" && s.notes && (
            <div className="text-xs text-[#8a857d] pt-2 border-t border-white/5">Notes: {s.notes}</div>
          )}
        </div>
      ))}
    </div>
  );
}

function StatusBadge({ status }) {
  const cls = STATUS_COLORS[status] || "bg-zinc-800 text-zinc-400 border-zinc-700";
  return <div className={`inline-block text-[10px] tracking-wider uppercase px-2 py-0.5 border mt-1 ${cls}`}>{status}</div>;
}

function Empty({ text }) {
  return <div className="p-10 text-center text-[#8a857d] text-sm border border-white/5 bg-[#0a0a0a]">{text}</div>;
}

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}
